// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { formatBtc } from '../format.js';
import type { BalancePanelModel } from '../balance.js';
import '../styles/balance-panel.css';

/**
 * COB-355: the one balance headline used by every money surface. Renders an
 * eyebrow label, the spendable figure as the page's hero BTC amount (the
 * shared `.btc-hero-figure` primitive + a separate unit span), and — when
 * there are still-confirming funds — a muted "+X BTC awaiting confirmation"
 * sub-note. Presentational and pure: callers derive `headlineSats` /
 * `pendingSats` via `balancePanelModel` (or `walletBalancePanel`) and hand
 * them in, so the "lead with spendable" rule lives in `balance.ts`, not here.
 *
 * A `null` headline renders the em-dash placeholder (balance unavailable /
 * still loading) with no unit and no sub-note.
 */
export function BalancePanel({
  label,
  headlineSats,
  pendingSats,
}: BalancePanelModel & { label: string }) {
  return (
    <div class="balance-panel">
      <div class="balance-panel-label">{label}</div>
      <div class="balance-panel-figure btc-hero-figure">
        {headlineSats === null ? (
          '—'
        ) : (
          <>
            {formatBtc(headlineSats)}
            <span class="btc-hero-unit">BTC</span>
          </>
        )}
      </div>
      {pendingSats !== null && (
        <p class="balance-panel-note tabular-nums">
          +{formatBtc(pendingSats)} BTC awaiting confirmation
        </p>
      )}
    </div>
  );
}
